interface SkeletonProps {
  className?: string;
}

export function SkeletonBlock({ className = "" }: SkeletonProps) {
  return (
    <div
      className={`animate-pulse rounded-lg bg-background-200/70 ${className}`}
    />
  );
}

export function SkeletonText({ lines = 1, className = "" }: { lines?: number; className?: string }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonBlock
          key={i}
          className={`h-3 ${i === lines - 1 && lines > 1 ? "w-3/4" : "w-full"}`}
        />
      ))}
    </div>
  );
}

export function SkeletonCircle({ size = 40 }: { size?: number }) {
  return (
    <SkeletonBlock
      className="rounded-full flex-shrink-0"
      style={{ width: size, height: size }}
    />
  );
}

export function SkeletonCard({ className = "" }: SkeletonProps) {
  return (
    <div className={`bg-white rounded-2xl p-4 space-y-3 ${className}`}>
      {/* title */}
      <div className="flex items-center justify-between">
        <SkeletonBlock className="h-3 w-20" />
        <SkeletonBlock className="h-3 w-12" />
      </div>
      {/* big number */}
      <SkeletonBlock className="h-8 w-40" />
      {/* stats row */}
      <div className="flex gap-2 pt-3 border-t border-background-100">
        <SkeletonBlock className="flex-1 h-14 rounded-xl" />
        <SkeletonBlock className="flex-1 h-14 rounded-xl" />
        <SkeletonBlock className="flex-1 h-14 rounded-xl" />
      </div>
    </div>
  );
}

export function SkeletonDecisionCard() {
  return (
    <div className="px-4 mt-3">
      <div className="bg-white rounded-card p-4 space-y-3 border-l-[3px] border-l-background-200">
        {/* header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SkeletonBlock className="h-5 w-12 rounded" />
            <SkeletonBlock className="h-4 w-16" />
            <SkeletonBlock className="h-3 w-14" />
          </div>
          <SkeletonBlock className="h-4 w-20 rounded" />
        </div>
        {/* reason text */}
        <SkeletonText lines={2} />
        {/* perspective bars */}
        <div className="grid grid-cols-4 gap-1.5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="text-center space-y-1">
              <SkeletonBlock className="h-2 w-8 mx-auto" />
              <SkeletonBlock className="h-1 w-full" />
              <SkeletonBlock className="h-2 w-6 mx-auto" />
            </div>
          ))}
        </div>
        {/* footer */}
        <div className="flex items-center justify-between pt-3 border-t border-background-100">
          <SkeletonBlock className="h-3 w-16" />
          <SkeletonBlock className="h-3 w-16" />
          <SkeletonBlock className="h-3 w-16" />
        </div>
      </div>
    </div>
  );
}

export function SkeletonRealtimePanel() {
  return (
    <div className="space-y-3 mt-3">
      {/* terminal block */}
      <div className="px-4">
        <div className="bg-[#1A1D28] rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SkeletonBlock className="h-6 w-6 rounded-md bg-white/[0.08]" />
              <SkeletonBlock className="h-4 w-20 bg-white/[0.08]" />
            </div>
            <SkeletonBlock className="h-4 w-14 bg-white/[0.08]" />
          </div>
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <SkeletonBlock className="h-3 w-3 bg-white/[0.06]" />
                <SkeletonBlock className="h-3 w-10 bg-white/[0.06]" />
                <SkeletonBlock className={`h-3 bg-white/[0.06] ${i === 0 ? "w-3/4" : i === 1 ? "w-2/3" : i === 2 ? "w-1/2" : "w-3/5"}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* positions row */}
      <div className="px-4">
        <div className="flex items-center justify-between mb-2">
          <SkeletonBlock className="h-4 w-16" />
          <SkeletonBlock className="h-3 w-16" />
        </div>
        <div className="flex gap-2 overflow-hidden">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex-shrink-0 w-[130px] bg-white rounded-card p-3 space-y-2">
              <div className="flex justify-between">
                <SkeletonBlock className="h-3 w-16" />
                <SkeletonBlock className="h-3 w-10" />
              </div>
              <SkeletonBlock className="h-2 w-12" />
              <SkeletonBlock className="h-5 w-20" />
              <SkeletonBlock className="h-2 w-10" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function DashboardSkeleton() {
  return (
    <div className="pb-[80px]">
      {/* Status bar skeleton */}
      <div className="flex items-center justify-between px-4 pt-3 pb-1">
        <div className="flex items-center gap-2">
          <SkeletonBlock className="h-3 w-24" />
          <SkeletonBlock className="h-5 w-14 rounded-full" />
          <SkeletonBlock className="h-4 w-10 rounded" />
        </div>
        <SkeletonBlock className="h-4 w-10 rounded-full" />
      </div>

      {/* Asset card */}
      <SkeletonCard className="mx-4 mt-1" />

      {/* AI decision section header */}
      <div className="mt-3 px-4 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <SkeletonBlock className="h-4 w-20" />
          <SkeletonBlock className="h-1.5 w-1.5 rounded-full" />
        </div>
        <SkeletonBlock className="h-1.5 w-12" />
      </div>

      {/* Decision card */}
      <SkeletonDecisionCard />

      {/* Realtime panel */}
      <SkeletonRealtimePanel />

      {/* Broadcast bar */}
      <div className="px-4 mt-3">
        <div className="bg-background-50 rounded-xl px-3.5 py-2.5 flex items-center gap-2">
          <SkeletonBlock className="h-3 w-3 flex-shrink-0" />
          <SkeletonBlock className="h-3 flex-1" />
        </div>
      </div>

      {/* Disclaimer */}
      <div className="px-4 mt-3 mb-4">
        <SkeletonBlock className="h-2 w-48 mx-auto" />
      </div>
    </div>
  );
}